import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { voucherEndpoints } from '../api-comps';
import type { VoucherBatchRow } from '../api-comps';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * Comp vouchers (D-373): "good for any one game, once".
 *
 * A batch is made for an occasion — 200 for a community night — each voucher
 * with its own code and QR, printed eight to a page or exported for a mail
 * merge. Redeemed on the website or at the window, a voucher becomes an
 * ordinary comp ticket with the batch's reason, so comp reports and the guest
 * list count it. Which games: the batch's season, never a game ticked
 * "Excluded from vouchers" on its Overview tab, nothing after the expiry.
 */
export default function VouchersPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [seasonId, setSeasonId] = useState('');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const options = useQuery({ queryKey: ['vouchers', 'options'], queryFn: () => voucherEndpoints.options(), enabled: can('vouchers.manage') });
  const batches = useQuery({ queryKey: ['vouchers', 'batches', seasonId], queryFn: () => voucherEndpoints.batches(seasonId || undefined), enabled: can('vouchers.manage') });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['vouchers'] });
  };
  const onError = (cause: ApiError) => setError(cause.message);

  if (!can('vouchers.manage')) return <NotPermitted permission="vouchers.manage" />;
  const rows = batches.data?.batches ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Comp vouchers"
        description="Good for any one game of a season, once. Games ticked “Excluded from vouchers” on their Overview tab are refused. A redeemed voucher is an ordinary comp ticket with the batch's reason."
        actions={
          <div className="flex items-center gap-3">
            <select aria-label="Season" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className={INPUT}>
              <option value="">Every season</option>
              {(options.data?.seasons ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {!creating ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                New batch
              </Button>
            ) : null}
          </div>
        }
      >
        {creating ? (
          <NewBatch
            seasons={options.data?.seasons ?? []}
            reasons={options.data?.comp_reasons ?? []}
            onDone={(batch) => {
              setCreating(false);
              setOpen(batch.id);
              refresh();
            }}
            onCancel={() => setCreating(false)}
            onError={onError}
          />
        ) : null}
        <Table headers={['Batch', 'Season', 'Vouchers', 'Used', 'Expires', '']}>
          {rows.length === 0 ? <EmptyRow columns={6}>{batches.isLoading ? 'Loading…' : 'No voucher batches yet.'}</EmptyRow> : null}
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-line">
              <td className="px-5 py-2.5 font-medium text-ink-900">
                {row.name} {row.voided_at ? <Badge tone="bad">voided</Badge> : null}
              </td>
              <td className="px-5 py-2.5 text-ink-600">{row.season_name}</td>
              <td className="tabular px-5 py-2.5">
                {row.quantity} × admits {row.admits}
              </td>
              <td className="tabular px-5 py-2.5">{row.redeemed ?? 0}</td>
              <td className="px-5 py-2.5">{row.effective_expires_on}</td>
              <td className="px-5 py-2.5 text-right">
                <Button onClick={() => setOpen(open === row.id ? null : row.id)}>{open === row.id ? 'Close' : 'Open'}</Button>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
      {open ? <BatchDetail id={open} onChanged={refresh} onError={onError} /> : null}
    </div>
  );
}

function NewBatch({ seasons, reasons, onDone, onCancel, onError }: { seasons: { id: string; name: string }[]; reasons: { id: string; code: string; name: string }[]; onDone: (batch: VoucherBatchRow) => void; onCancel: () => void; onError: (error: ApiError) => void }) {
  const [name, setName] = useState('');
  const [season, setSeason] = useState(seasons[0]?.id ?? '');
  const [reason, setReason] = useState(reasons.find((r) => r.code === 'VOUCHER')?.id ?? '');
  const [quantity, setQuantity] = useState('100');
  const [admits, setAdmits] = useState('1');
  const [ticketType, setTicketType] = useState('ADULT');
  const [zones, setZones] = useState('');
  const [expires, setExpires] = useState('');
  const [terms, setTerms] = useState('');
  const [prefix, setPrefix] = useState('');
  const create = useMutation({
    mutationFn: () =>
      voucherEndpoints.create({
        name: name.trim(),
        season_id: season,
        comp_reason_id: reason || null,
        quantity: Number(quantity),
        admits: Number(admits),
        ticket_type_code: ticketType.trim().toUpperCase(),
        price_zone_codes: zones.trim() ? zones.split(',').map((z) => z.trim().toUpperCase()).filter(Boolean) : null,
        expires_on: expires || null,
        terms_text: terms.trim() || null,
        prefix: prefix.trim().toUpperCase(),
        length: 8,
      }),
    onSuccess: (result) => onDone(result.batch),
    onError,
  });
  return (
    <form
      className="space-y-4 border-b border-line px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Name" htmlFor="vb-name" hint="What it was for: printed on each voucher">
          <input id="vb-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Community night" className={`w-64 ${INPUT}`} />
        </Field>
        <Field label="Season" htmlFor="vb-season">
          <select id="vb-season" value={season} onChange={(e) => setSeason(e.target.value)} className={INPUT}>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Comp reason" htmlFor="vb-reason">
          <select id="vb-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT}>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="How many" htmlFor="vb-qty">
          <input id="vb-qty" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
        </Field>
        <Field label="Admits" htmlFor="vb-admits" hint="People per voucher">
          <input id="vb-admits" value={admits} onChange={(e) => setAdmits(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
        </Field>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Ticket type code" htmlFor="vb-type" hint="Each game's own type with this code is issued">
          <input id="vb-type" value={ticketType} onChange={(e) => setTicketType(e.target.value)} className={`w-32 ${INPUT}`} />
        </Field>
        <Field label="Price zones" htmlFor="vb-zones" hint="Codes, comma separated; empty is any (e.g. GA only)">
          <input id="vb-zones" value={zones} onChange={(e) => setZones(e.target.value)} placeholder="GA" className={`w-40 ${INPUT}`} />
        </Field>
        <Field label="Use by" htmlFor="vb-expires" hint="Empty: the end of the season">
          <input id="vb-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Code prefix" htmlFor="vb-prefix" hint="Optional, up to 12 characters">
          <input id="vb-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} maxLength={12} className={`w-28 ${INPUT}`} />
        </Field>
      </div>
      <Field label="Terms" htmlFor="vb-terms" hint="Printed on each voucher">
        <textarea id="vb-terms" value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} maxLength={600} placeholder="Not valid for fireworks nights or opening day. No cash value." className={`w-full ${INPUT}`} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!name.trim() || !season || !quantity || create.isPending}>
          {create.isPending ? 'Making codes…' : 'Make vouchers'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

const STATUS_TONE = { OUTSTANDING: 'neutral', REDEEMED: 'ok', EXPIRED: 'warn', VOIDED: 'bad' } as const;

function BatchDetail({ id, onChanged, onError }: { id: string; onChanged: () => void; onError: (error: ApiError) => void }) {
  const queryClient = useQueryClient();
  const report = useQuery({ queryKey: ['vouchers', 'report', id], queryFn: () => voucherEndpoints.report(id) });
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['vouchers', 'report', id] });
    onChanged();
  };
  const voidBatch = useMutation({ mutationFn: (reason: string) => voucherEndpoints.voidBatch(id, reason), onSuccess: done, onError });
  const voidOne = useMutation({ mutationFn: ({ voucherId, reason }: { voucherId: string; reason: string }) => voucherEndpoints.voidVoucher(id, voucherId, reason), onSuccess: done, onError });
  if (!report.data) return <Panel title="Batch">{report.isLoading ? <p className="px-5 py-4 text-sm text-ink-500">Loading…</p> : null}</Panel>;
  const { batch, counts, vouchers, games } = report.data;
  const excluded = games.filter((g) => !g.eligible);
  return (
    <Panel
      title={batch.name}
      description={`${batch.season_name} · ${batch.comp_reason_name} · admits ${batch.admits} · ${batch.ticket_type_code}${batch.price_zone_codes ? ` in ${batch.price_zone_codes.join(', ')}` : ''} · use by ${batch.effective_expires_on}`}
      actions={
        <div className="flex flex-wrap gap-2">
          <a className="rounded border border-line px-3 py-1.5 text-sm" href={voucherEndpoints.sheetUrl(id)} target="_blank" rel="noreferrer">
            Print outstanding
          </a>
          <a className="rounded border border-line px-3 py-1.5 text-sm" href={voucherEndpoints.csvUrl(id)}>
            Export CSV
          </a>
          {!batch.voided_at ? (
            <Button
              variant="danger"
              disabled={voidBatch.isPending}
              onClick={() => {
                const reason = window.prompt('Why void every unused voucher in this batch? Used ones stay used.');
                if (reason) voidBatch.mutate(reason);
              }}
            >
              Void batch
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-wrap gap-4 border-b border-line px-5 py-3 text-sm">
        <span>Issued {counts.issued}</span>
        <span className="text-ok">Redeemed {counts.redeemed}</span>
        <span>Outstanding {counts.outstanding}</span>
        <span className="text-warn">Expired {counts.expired}</span>
        <span className="text-bad">Voided {counts.voided}</span>
      </div>
      {excluded.length > 0 ? (
        <p className="border-b border-line px-5 py-3 text-xs text-ink-500">
          Not accepted: {excluded.map((g) => `${g.title} (${g.reason ?? 'excluded'})`).join('; ')}
        </p>
      ) : null}
      <Table headers={['#', 'Code', 'Status', 'Game', 'Order', 'Redeemed', '']}>
        {vouchers.map((v) => (
          <tr key={v.id} className="border-b border-line">
            <td className="tabular px-5 py-2">{v.sequence}</td>
            <td className="px-5 py-2 font-mono">{v.code}</td>
            <td className="px-5 py-2">
              <Badge tone={STATUS_TONE[v.status]}>{v.status.toLowerCase()}</Badge>
            </td>
            <td className="px-5 py-2">{v.event_title ? `${v.event_title} · ${formatWhen(v.starts_at)}` : '—'}</td>
            <td className="px-5 py-2">{v.order_number ? <a href={`/orders/${v.order_id}`}>{v.order_number}</a> : '—'}</td>
            <td className="px-5 py-2">{v.redeemed_at ? `${formatWhen(v.redeemed_at)} · ${v.channel === 'BOX_OFFICE' ? 'window' : 'online'}` : '—'}</td>
            <td className="px-5 py-2 text-right">
              {v.status === 'OUTSTANDING' ? (
                <Button
                  disabled={voidOne.isPending}
                  onClick={() => {
                    const reason = window.prompt(`Why void voucher ${v.code}?`);
                    if (reason) voidOne.mutate({ voucherId: v.id, reason });
                  }}
                >
                  Void
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}
